<script lang="ts">
  import { observe } from "$lib/actions/observe";
  import { sanitizeRich } from "$lib/utils/sanitize";

  // Lighthouse-style score band: a row of circular score rings (0–100) plus
  // optional "fraction" pills (e.g. 3/3). Metric shape is data-driven from YAML —
  // a numeric `value` renders a ring, a string `badge` renders a pill.
  // `note` explains the metrics below the row; `verifyHref`/`verifyText` link to a live measurement.
  let { sectionLabel = "", headline = "", description = "", metrics = [] as Array<{ value?: number; badge?: string; label: string }>, note = "", verifyHref = "", verifyText = "" } = $props();

  // Ring geometry (SVG viewBox is 0 0 100 100).
  const R = 44;
  const CIRC = 2 * Math.PI * R;

  // Stroke offset for a score: 0 = full ring, CIRC = empty.
  const offsetFor = (v: number) => CIRC * (1 - Math.max(0, Math.min(100, v)) / 100);

  // Lighthouse colour thresholds: ≥90 pass (green), 50–89 average, <50 fail.
  const tierFor = (v: number) => (v >= 90 ? "pass" : v >= 50 ? "avg" : "fail");

  // A pill is "full" (green) when current === total, e.g. "3/3".
  const isFull = (badge: string) => {
    const [cur, total] = badge.split("/").map((n) => parseInt(n, 10));
    return Number.isFinite(cur) && Number.isFinite(total) ? cur >= total : true;
  };
</script>

<section class="lh-section">
  <div class="container">
    <div class="lh-head fade-up" use:observe>
      {#if sectionLabel}
        <span class="section-label">{sectionLabel}</span>
      {/if}
      {#if headline}
        <h2>{@html sanitizeRich(headline)}</h2>
      {/if}
      {#if description}
        <p class="lh-desc">{description}</p>
      {/if}
    </div>

    <div class="lh-row fade-up" use:observe style="transition-delay:.1s">
      {#each metrics as m}
        <div class="lh-item">
          <div class="lh-visual">
            {#if typeof m.value === "number"}
              <div class="lh-ring lh-ring--{tierFor(m.value)}">
                <svg viewBox="0 0 100 100" aria-hidden="true">
                  <circle class="lh-track" cx="50" cy="50" r={R} />
                  <circle class="lh-bar" cx="50" cy="50" r={R} transform="rotate(-90 50 50)" style="--circ:{CIRC};--target:{offsetFor(m.value)}" />
                </svg>
                <span class="lh-num">{m.value}</span>
              </div>
            {:else if m.badge}
              <div class="lh-pill lh-pill--{isFull(m.badge) ? 'pass' : 'avg'}">
                <span class="lh-dot" aria-hidden="true"></span>
                <span class="lh-frac">{m.badge}</span>
              </div>
            {/if}
          </div>
          <div class="lh-label">{m.label}</div>
        </div>
      {/each}
    </div>

    {#if note || verifyHref}
      <div class="lh-foot fade-up" use:observe>
        {#if note}
          <p>{note}</p>
        {/if}
        {#if verifyHref}
          <a href={verifyHref} target="_blank" rel="noopener">{verifyText || verifyHref} →</a>
        {/if}
      </div>
    {/if}
  </div>
</section>

<style>
  .lh-section {
    padding: clamp(3rem, 7vw, 5rem) 0;
    border-bottom: 0.5px solid var(--border);

    /* Score palette — local to this component (no global green token exists). */
    --score-pass: oklch(70% 0.18 152);
    --score-pass-track: oklch(94% 0.05 152);
    --score-pass-bg: oklch(96% 0.04 152);
    --score-avg: var(--accent);
    --score-avg-track: oklch(94% 0.05 75);
    --score-fail: oklch(60% 0.2 25);
    --score-fail-track: oklch(94% 0.05 25);
  }
  :global(html.dark) .lh-section {
    --score-pass: oklch(74% 0.16 152);
    --score-pass-track: oklch(32% 0.04 152);
    --score-pass-bg: oklch(28% 0.05 152);
    --score-avg-track: oklch(32% 0.04 75);
    --score-fail-track: oklch(32% 0.04 25);
  }

  .lh-head {
    text-align: center;
    max-width: 680px;
    margin: 0 auto clamp(2rem, 4vw, 3rem);
  }
  .lh-head h2 {
    font-family: var(--font-display);
    font-size: clamp(1.6rem, 3.5vw, 2.4rem);
    line-height: 1.15;
    letter-spacing: -0.02em;
    margin: 0.5rem 0 0;
  }
  .lh-desc {
    color: var(--muted);
    max-width: 60ch;
    margin: 0.9rem auto 0;
    line-height: 1.6;
  }

  .lh-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    gap: clamp(28px, 5vw, 72px);
  }
  .lh-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
  }
  .lh-visual {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: clamp(78px, 9vw, 92px);
  }

  /* Rings */
  .lh-ring {
    position: relative;
    width: clamp(78px, 9vw, 92px);
    aspect-ratio: 1;
  }
  .lh-ring--pass {
    --c: var(--score-pass);
    --track: var(--score-pass-track);
  }
  .lh-ring--avg {
    --c: var(--score-avg);
    --track: var(--score-avg-track);
  }
  .lh-ring--fail {
    --c: var(--score-fail);
    --track: var(--score-fail-track);
  }
  .lh-ring svg {
    display: block;
    width: 100%;
    height: 100%;
    fill: none;
  }
  .lh-track {
    stroke: var(--track);
    stroke-width: 7;
  }
  .lh-bar {
    stroke: var(--c);
    stroke-width: 7;
    stroke-linecap: round;
    stroke-dasharray: var(--circ);
    /* Resting state = final score, so the ring is correct without JS (prerender). */
    stroke-dashoffset: var(--target);
  }
  /* Draw-in once the row scrolls into view (observe adds .visible). */
  :global(.lh-row.fade-up.visible) .lh-bar {
    animation: lh-draw 1.1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }
  @keyframes lh-draw {
    from {
      stroke-dashoffset: var(--circ);
    }
    to {
      stroke-dashoffset: var(--target);
    }
  }
  .lh-num {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: clamp(1.1rem, 2.5vw, 1.45rem);
    letter-spacing: -0.01em;
    color: var(--c);
  }

  /* Pill */
  .lh-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 18px;
    height: clamp(40px, 5vw, 48px);
    border-radius: 999px;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: clamp(1rem, 2.2vw, 1.25rem);
    letter-spacing: -0.01em;
  }
  .lh-pill--pass {
    background: var(--score-pass-bg);
    color: var(--score-pass);
  }
  .lh-pill--avg {
    background: var(--accent-bg);
    color: var(--accent);
  }
  .lh-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: currentColor;
  }

  .lh-label {
    font-size: clamp(0.9rem, 1.6vw, 1.05rem);
    font-weight: 500;
    line-height: 1.25;
    color: var(--fg);
    max-width: 11ch;
  }

  .lh-foot {
    text-align: center;
    max-width: 60ch;
    margin: clamp(2rem, 4vw, 3rem) auto 0;
    color: var(--muted);
    font-size: 0.9rem;
    line-height: 1.6;
  }
  .lh-foot a {
    display: inline-block;
    margin-top: 0.5rem;
    font-weight: 500;
  }

  @media (prefers-reduced-motion: reduce) {
    :global(.lh-row.fade-up.visible) .lh-bar {
      animation: none;
    }
  }
</style>
